import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Send, Clock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConsultationSection() {
  const { t } = useLanguage();
  const section = t("consultation");
  const labels = section.labels || {};
  const placeholders = section.placeholders || {};

  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", description: "" });
  const [submitting, setSubmitting] = useState(false);

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email || !form.description) {
      toast.error(section.requiredMsg);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/consultations`, {
        full_name: `${form.first_name} ${form.last_name}`.trim(),
        email: form.email,
        phone: form.phone,
        description: form.description,
        case_type: "General Inquiry",
        contact_method: "Email",
        preferred_date: "",
        preferred_time: "",
      });
      toast.success(section.successMsg);
      setForm({ first_name: "", last_name: "", email: "", phone: "", description: "" });
    } catch {
      toast.error(section.errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = "rounded-none border-0 border-b border-[#d5cfc6] bg-transparent focus:border-[#1a5c4a] focus:ring-0 h-12 text-[#1a1a2e] placeholder:text-[#a09890] px-0";

  return (
    <section data-testid="consultation-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-24">
          <Reveal animation="reveal-left" className="lg:col-span-2">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a1a2e] leading-[1.1] tracking-[-0.02em] mb-6">
              {section.heading}
            </h2>
            <p className="text-[#4a4540] text-base leading-relaxed mb-8">{section.description}</p>
            <div className="flex items-start gap-3 text-sm text-[#4a4540]">
              <Clock className="h-5 w-5 text-[#1a5c4a] mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-[#1a1a2e] mb-1">{section.openingHoursTitle}</p>
                <p>{section.weekdays}</p>
                <p>{section.saturday}</p>
              </div>
            </div>
          </Reveal>

          <Reveal animation="reveal-right" className="lg:col-span-3">
            <form onSubmit={handleSubmit} data-testid="consultation-form" className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-sm font-medium text-[#1a1a2e] mb-2">{labels.firstName}</label>
                  <Input data-testid="consult-first-name" value={form.first_name} onChange={(e) => updateField("first_name", e.target.value)} placeholder={placeholders.firstName} className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1a1a2e] mb-2">{labels.lastName}</label>
                  <Input data-testid="consult-last-name" value={form.last_name} onChange={(e) => updateField("last_name", e.target.value)} placeholder={placeholders.lastName} className={inputClass} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1a1a2e] mb-2">{labels.email}</label>
                <Input data-testid="consult-email" type="email" value={form.email} onChange={(e) => updateField("email", e.target.value)} placeholder={placeholders.email} className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1a1a2e] mb-2">{labels.phone}</label>
                <Input data-testid="consult-phone" value={form.phone} onChange={(e) => updateField("phone", e.target.value)} placeholder={placeholders.phone} className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1a1a2e] mb-2">{labels.describe}</label>
                <Textarea data-testid="consult-description" value={form.description} onChange={(e) => updateField("description", e.target.value)} placeholder={placeholders.describe} className="rounded-none border-0 border-b border-[#d5cfc6] bg-transparent focus:border-[#1a5c4a] focus:ring-0 min-h-[100px] text-[#1a1a2e] placeholder:text-[#a09890] px-0 resize-none" />
              </div>
              <Button
                type="submit"
                data-testid="consult-submit-btn"
                disabled={submitting}
                className="bg-[#1a5c4a] text-white hover:bg-[#154a3c] rounded-full px-10 py-6 text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50"
              >
                {submitting ? section.sending : (<><Send className="h-4 w-4 mr-2" />{section.submitBtn}</>)}
              </Button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
